import { Device } from '@capacitor/device';
import { supabase, supabaseConfigured } from '@/lib/supabaseClient';
import { isWeb } from '@/lib/platform';
import { safeLocalStorage } from '@/lib/browserStorage';

const DEVICE_ID_KEY = 'drilllog_device_id';
const HEARTBEAT_MS = 60_000;

function fallbackDeviceId() {
  let value = safeLocalStorage.getItem(DEVICE_ID_KEY);
  if (!value) {
    value = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    safeLocalStorage.setItem(DEVICE_ID_KEY, value);
  }
  return value;
}

function browserDetails() {
  const ua = navigator.userAgent || '';
  const uaData = navigator.userAgentData;
  const browser = uaData?.brands?.find?.((b) => /Chrome|Chromium|Edge/i.test(b.brand))?.brand || (/Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser');
  const os = /Windows/i.test(ua) ? 'Windows' : /Mac OS X/i.test(ua) ? 'macOS' : /Android/i.test(ua) ? 'Android' : /iPhone|iPad|iPod/i.test(ua) ? 'iOS' : /Linux/i.test(ua) ? 'Linux' : 'Unknown OS';
  return { browser, os, name: `${browser} on ${os}` };
}

export async function getDeviceIdentity() {
  if (isWeb) {
    const details = browserDetails();
    const device = { device_id: fallbackDeviceId(), device_name: details.name, manufacturer: 'Browser', model: details.browser, os: details.os, os_version: 'Unknown', platform: 'web' };
    safeLocalStorage.setItem('drilllog_device_name', device.device_name);
    return device;
  }
  try {
    const [idInfo, info] = await Promise.all([Device.getId(), Device.getInfo()]);
    const identifier = idInfo?.identifier || fallbackDeviceId();
    const name = info?.name || `${info?.manufacturer || 'Device'} ${info?.model || ''}`.trim() || 'Unknown device';
    safeLocalStorage.setItem(DEVICE_ID_KEY, identifier);
    safeLocalStorage.setItem('drilllog_device_name', name);
    return { device_id: identifier, device_name: name, manufacturer: info?.manufacturer || 'Unknown', model: info?.model || 'Unknown', os: info?.operatingSystem || info?.platform || 'Unknown', os_version: info?.osVersion || 'Unknown', platform: info?.platform || 'unknown' };
  } catch {
    return { device_id: fallbackDeviceId(), device_name: 'Unknown device', manufacturer: 'Unknown', model: 'Unknown', os: 'Unknown', os_version: 'Unknown', platform: 'unknown' };
  }
}

async function callDeviceFunction(action, device) {
  if (!supabaseConfigured || !supabase) return { active: true };
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) return { active: true };
  const { data, error } = await supabase.functions.invoke('device-session', {
    body: { action, device },
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (error) throw error;
  return data || { active: true };
}

export async function registerDeviceSession(userId) {
  if (!userId || !supabaseConfigured || !supabase) return null;
  const device = await getDeviceIdentity();
  try {
    return await callDeviceFunction('heartbeat', device);
  } catch (error) {
    // Keep login usable if the device-session function has not been deployed yet.
    const now = new Date().toISOString();
    const { data, error: dbError } = await supabase.from('drilllog_device_sessions').upsert({ ...device, user_id: userId, last_seen_at: now, revoked_at: null, updated_at: now }, { onConflict: 'user_id,device_id' }).select().single();
    if (dbError) throw error || dbError;
    return data;
  }
}

export async function heartbeatDeviceSession(userId) {
  if (!userId || !supabaseConfigured || !supabase) return { active: true };
  const device = await getDeviceIdentity();
  try {
    return await callDeviceFunction('heartbeat', device);
  } catch (error) {
    const { data, error: readError } = await supabase.from('drilllog_device_sessions').select('revoked_at').eq('user_id', userId).eq('device_id', device.device_id).maybeSingle();
    if (readError) throw error || readError;
    if (data?.revoked_at) return { active: false };
    await supabase.from('drilllog_device_sessions').upsert({ ...device, user_id: userId, last_seen_at: new Date().toISOString(), updated_at: new Date().toISOString() }, { onConflict: 'user_id,device_id' });
    return { active: true };
  }
}

export function startDeviceHeartbeat(userId, onRevoked) {
  let stopped = false; let timer;
  const tick = async () => {
    if (stopped) return;
    try { const result = await heartbeatDeviceSession(userId); if (!result.active && !stopped) onRevoked?.(); } catch (error) { console.warn('[DrillLogPro] device heartbeat skipped:', error?.message || error); }
    if (!stopped) timer = window.setTimeout(tick, HEARTBEAT_MS);
  };
  void registerDeviceSession(userId).then(() => { if (!stopped) timer = window.setTimeout(tick, HEARTBEAT_MS); }).catch((error) => { console.warn('[DrillLogPro] device registration skipped:', error?.message || error); if (!stopped) timer = window.setTimeout(tick, HEARTBEAT_MS); });
  return () => { stopped = true; if (timer) window.clearTimeout(timer); };
}
